import { Client } from '@microsoft/microsoft-graph-client';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { config } from './config';
import { logger } from './logger';
import { setAuthHeaderProvider as setApiAuthHeaderProvider } from './api';

// Get auth header provider reference (set by AuthContext)
let authHeaderProvider: (() => Promise<string>) | null = null;

// MSAL configuration for multi-tenant app with admin consent flow
const msalConfig = {
  auth: {
    clientId: config.azure.clientId,
    authority: config.azure.authority,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    allowPlatformBroker: false, // Disable native broker for web apps
    loggerOptions: {
      loggerCallback: (level: any, message: string, containsPii: boolean) => {
        if (containsPii) return;
        logger.debug(`MSAL ${level}: ${message}`, undefined, 'MSAL');
      }
    }
  }
};

// Scopes requested from the sign-in ("Apps by exobe") app registration.
// This app is SIGN-IN ONLY: it must not request directory permissions it does not own.
// Directory reads (user/group search, presence, photos) are served by the backend via the
// dedicated Presence/Contact Sync app registrations, not by this client-side token.
const graphScopes = ['User.Read'];

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL when module loads
let msalInitialized = false;
async function initializeMsal(): Promise<void> {
  if (!msalInitialized) {
    await msalInstance.initialize();
    
    // Handle redirect result if returning from authentication
    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        logger.authSuccess('redirect', response.account?.username);
      }
    } catch (error) {
      logger.authError('redirect handling', error);
    }
    
    msalInitialized = true;
    logger.info('MSAL instance initialized', undefined, 'MSAL');
  }
}

// Initialize Graph client
let graphClient: Client | null = null;

// Token provider function type for Teams SSO integration
type TokenProvider = () => Promise<string>;

// Store the token provider if provided (e.g., from Teams SSO)
let externalTokenProvider: TokenProvider | null = null;

// Mutex to prevent concurrent authentication attempts
let initializationPromise: Promise<Client> | null = null;

// Track if we're in Teams context (to avoid popup auth)
let isTeamsContext = false;

// Set Teams context flag (called by AuthContext)
export function setTeamsContext(inTeams: boolean): void {
  isTeamsContext = inTeams;
  logger.debug('Teams context updated', { isTeamsContext: inTeams }, 'GRAPH');
}

// Set external token provider (e.g., Teams SSO token)
export function setGraphTokenProvider(tokenProvider: TokenProvider | null): void {
  externalTokenProvider = tokenProvider;
  // Reset graph client to force re-initialization with new token provider
  graphClient = null;
  initializationPromise = null; // Reset initialization promise
  logger.debug('Graph token provider updated', { hasProvider: !!tokenProvider }, 'GRAPH');
}

// Set auth header provider for backend API calls (set by AuthContext)
export function setGraphAuthHeaderProvider(provider: (() => Promise<string>) | null): void {
  authHeaderProvider = provider;
}

// Initialize the Graph client with authentication
// If a token provider is set (e.g., Teams SSO), it will be used instead of MSAL
export async function initializeGraphClient(): Promise<Client> {
  if (graphClient) return graphClient;
  
  // If initialization is already in progress, wait for it
  if (initializationPromise) {
    logger.debug('Graph client initialization already in progress, waiting...', undefined, 'GRAPH');
    return initializationPromise;
  }

  // Start initialization and store the promise
  initializationPromise = (async (): Promise<Client> => {
    try {
      logger.debug('Initializing Graph client', { useExternalToken: !!externalTokenProvider }, 'GRAPH');
      
      let accessToken: string;

      // If external token provider is available (e.g., Teams SSO), use it
      if (externalTokenProvider) {
      logger.debug('Using external token provider (Teams SSO)', undefined, 'GRAPH');
      accessToken = await externalTokenProvider();
      logger.debug('External token acquired successfully', { tokenLength: accessToken.length }, 'GRAPH');
    } else {
      // Use MSAL for standard browser authentication
      logger.debug('Using MSAL browser authentication', undefined, 'GRAPH');
      
      // Ensure MSAL is initialized first
      await initializeMsal();
      
      // Try to get token silently
      const accounts = msalInstance.getAllAccounts();
      logger.debug('MSAL accounts found', { count: accounts.length }, 'GRAPH');

      if (accounts.length > 0) {
        logger.debug('Attempting silent token acquisition', undefined, 'GRAPH');
        try {
          const response = await msalInstance.acquireTokenSilent({
            scopes: graphScopes,
            account: accounts[0]!,
          });
          accessToken = response.accessToken;
          logger.debug('Silent token acquisition successful', undefined, 'GRAPH');
        } catch (error) {
          logger.debug('Silent token acquisition failed, trying popup', undefined, 'GRAPH');
          if (error instanceof InteractionRequiredAuthError) {
            // In Teams context, don't try popup - just fail gracefully
            if (isTeamsContext) {
              logger.warn('Cannot do interactive auth in Teams context - skipping', undefined, 'GRAPH');
              throw new Error('Graph authentication not available in Teams context. Please ensure admin consent is granted.');
            }
            
            // Try popup with more detailed request configuration
            logger.debug('Starting popup flow for token acquisition', undefined, 'GRAPH');
            try {
              const response = await msalInstance.acquireTokenPopup({
                scopes: graphScopes,
                prompt: 'select_account',
              });
              accessToken = response.accessToken;
              logger.debug('Popup token acquisition successful', undefined, 'GRAPH');
            } catch (popupError: any) {
              logger.error('Popup authentication failed', popupError, 'GRAPH');
              // If popup fails due to SPA configuration, provide specific guidance
              if (popupError.errorCode === 'invalid_request' && popupError.errorMessage?.includes('9002326')) {
                throw new Error('Azure AD app registration configuration issue. The app must be configured as a Single-Page Application (SPA) with proper redirect URIs. Please check Azure Portal → App Registrations → Authentication.');
              }
              throw popupError;
            }
          } else {
            throw error;
          }
        }
      } else {
        logger.debug('No accounts found, prompting for sign-in', undefined, 'GRAPH');
        
        // In Teams context, don't try popup - just fail gracefully
        if (isTeamsContext) {
          logger.warn('Cannot do interactive auth in Teams context - no accounts found', undefined, 'GRAPH');
          throw new Error('Graph authentication not available in Teams context. Please sign in through the web app first.');
        }
        
        // No accounts found, need to sign in
        try {
          const response = await msalInstance.acquireTokenPopup({
            scopes: graphScopes,
            prompt: 'select_account',
          });
          accessToken = response.accessToken;
          logger.debug('Sign-in successful', undefined, 'GRAPH');
        } catch (popupError: any) {
          logger.error('Sign-in failed', popupError, 'GRAPH');
          if (popupError.errorCode === 'invalid_request' && popupError.errorMessage?.includes('9002326')) {
            throw new Error('Azure AD app registration must be configured as Single-Page Application (SPA). Please check: Azure Portal → App Registrations → [Your App] → Authentication → Platform configurations → Single-page application');
          }
          throw popupError;
        }
      }
    }

    // Create Graph client with proper auth provider callback
    // For external tokens (Teams SSO), refresh token on each call
    graphClient = Client.init({
      authProvider: async (done) => {
        if (externalTokenProvider) {
          // For Teams SSO, get fresh token on each call
          try {
            const freshToken = await externalTokenProvider();
            done(null, freshToken);
          } catch (error: any) {
            done(error, null);
          }
        } else {
          // For MSAL, use cached token
          done(null, accessToken);
        }
      }
    });

    logger.debug('Graph client created successfully', undefined, 'GRAPH');
    return graphClient;
  } catch (error: any) {
    // Clear the initialization promise on error so it can be retried
    initializationPromise = null;
    graphClient = null;
    
    logger.error('Error initializing Graph client', {
      message: error.message,
      code: error.code,
      scopes: graphScopes
    }, 'GRAPH');
    
    // Provide more specific error messages for common issues
    if (error.message?.includes('redirect_uri')) {
      throw new Error('Redirect URI mismatch. Check Azure AD app registration.');
    } else if (error.message?.includes('consent')) {
      throw new Error('Admin consent required. Please contact your administrator.');
    } else {
      throw error;
    }
  }
  })();

  return initializationPromise;
}

// User interface for Entra ID users
export interface EntraUser {
  id: string;
  displayName: string;
  email: string;
  jobTitle?: string;
  department?: string;
  userPrincipalName: string;
  usageLocation?: string; // ISO 3166-1 alpha-2 country code (e.g., "SE", "US", "GB")
}

// Group interface for Entra ID groups
export interface EntraGroup {
  id: string;
  displayName: string;
  description?: string;
  mail?: string;
  mailEnabled: boolean;
  securityEnabled: boolean;
}

// Search users in Entra ID
export async function searchEntraUsers(query: string, maxResults: number = 50): Promise<EntraUser[]> {
  try {
    logger.debug('Starting user search', { query }, 'GRAPH');
    const client = await initializeGraphClient();
    
    // Search users with displayName or userPrincipalName containing the query
    const response = await client
      .api('/users')
      .filter(`startswith(displayName,'${query}') or startswith(userPrincipalName,'${query}')`)
      .select('id,displayName,userPrincipalName,mail,jobTitle,department,usageLocation')
      .top(maxResults)
      .get();

    const users = response.value.map((user: any) => ({
      id: user.id,
      displayName: user.displayName,
      email: user.mail || user.userPrincipalName,
      jobTitle: user.jobTitle,
      department: user.department,
      userPrincipalName: user.userPrincipalName,
      usageLocation: user.usageLocation,
    }));
    
    logger.debug('User search completed', { count: users.length }, 'GRAPH');
    return users;
  } catch (error: any) {
    logger.error('Error searching users', {
      message: error.message,
      code: error.code,
      query: query
    }, 'GRAPH');
    
    // Provide more specific error messages
    if (error.code === 'InvalidAuthenticationToken') {
      throw new Error('Authentication failed. Please sign in again.');
    } else if (error.code === 'Forbidden' || error.message?.includes('Insufficient privileges')) {
      throw new Error('Insufficient permissions. Admin consent may be required.');
    } else if (error.code === 'ThrottledRequest') {
      throw new Error('Too many requests. Please wait and try again.');
    } else {
      throw new Error(`Failed to search users: ${error.message || 'Unknown error'}`);
    }
  }
}

// Group search is served by the backend API (searchGroups in lib/api), which uses the multi-tenant
// Presence app scoped to the caller's own tenant. The old client-side /groups call rode the sign-in
// app's User.Read token, which cannot read groups, so it was removed.

// Get user details by ID
export async function getUserById(userId: string): Promise<EntraUser | null> {
  try {
    const client = await initializeGraphClient();
    
    const user = await client
      .api(`/users/${userId}`)
      .select('id,displayName,userPrincipalName,mail,jobTitle,department,usageLocation')
      .get();

    return {
      id: user.id,
      displayName: user.displayName,
      email: user.mail || user.userPrincipalName,
      jobTitle: user.jobTitle,
      department: user.department,
      userPrincipalName: user.userPrincipalName,
      usageLocation: user.usageLocation,
    };
  } catch (error) {
    console.error('Error getting user by ID:', error);
    return null;
  }
}

// Get current user's profile (including usageLocation for E.164 phone formatting)
export async function getCurrentUserProfile(): Promise<EntraUser | null> {
  try {
    const client = await initializeGraphClient();
    
    const user = await client
      .api('/me')
      .select('id,displayName,userPrincipalName,mail,jobTitle,department,usageLocation')
      .get();

    return {
      id: user.id,
      displayName: user.displayName,
      email: user.mail || user.userPrincipalName,
      jobTitle: user.jobTitle,
      department: user.department,
      userPrincipalName: user.userPrincipalName,
      usageLocation: user.usageLocation,
    };
  } catch (error) {
    console.error('Error getting current user profile:', error);
    return null;
  }
}

// Get group details by ID
export async function getGroupById(groupId: string): Promise<EntraGroup | null> {
  try {
    const client = await initializeGraphClient();
    
    const group = await client
      .api(`/groups/${groupId}`)
      .select('id,displayName,description,mail,mailEnabled,securityEnabled')
      .get();

    return {
      id: group.id,
      displayName: group.displayName,
      description: group.description,
      mail: group.mail,
      mailEnabled: group.mailEnabled,
      securityEnabled: group.securityEnabled,
    };
  } catch (error) {
    console.error('Error getting group by ID:', error);
    return null;
  }
}

// Check if user is authenticated
export async function isAuthenticated(): Promise<boolean> {
  await initializeMsal();
  const accounts = msalInstance.getAllAccounts();
  return accounts.length > 0;
}

// Check if Azure AD is properly configured (for multi-tenant app)
export async function isConfigured(): Promise<{ configured: boolean; needsConsent: boolean; tenantId?: string }> {
  // Ensure MSAL is initialized
  await initializeMsal();
  
  // For multi-tenant app, we just need to check if user is signed in
  const accounts = msalInstance.getAllAccounts();
  
  if (accounts.length === 0) {
    return { configured: false, needsConsent: true };
  }
  
  const account = accounts[0]!;
  const tenantId = account.tenantId;
  
  // TODO: Check if tenant is onboarded (has given admin consent)
  // For now, assume configured if user is signed in
  return {
    configured: true,
    needsConsent: false,
    tenantId
  };
}

// Get the admin consent URL for the signed-in admin's own tenant.
//
// Admin consent MUST be scoped to the caller's tenant. Using the `/common` authority would grant
// consent in whichever tenant the admin's browser session is signed into (e.g. the hosting tenant),
// so we resolve the tenant from the signed-in MSAL account and refuse to build a URL without it.
export async function getAdminConsentUrl(): Promise<string> {
  await initializeMsal();
  const accounts = msalInstance.getAllAccounts();
  const tenantId = accounts[0]?.tenantId;
  if (!tenantId) {
    throw new Error('Cannot build admin consent URL: no signed-in account tenant available.');
  }

  const baseUrl = `https://login.microsoftonline.com/${encodeURIComponent(tenantId)}/adminconsent`;
  const params = new URLSearchParams({
    client_id: msalConfig.auth.clientId,
    redirect_uri: `${window.location.origin}/admin-consent-callback`
  });

  return `${baseUrl}?${params.toString()}`;
}

// Sign out
export async function signOut(): Promise<void> {
  await initializeMsal();
  await msalInstance.logoutPopup();
  graphClient = null;
}

// Check Azure AD app registration configuration
export function checkAppRegistrationConfig(): { 
  clientId: string; 
  authority: string; 
  redirectUri: string; 
  recommendations: string[] 
} {
  const recommendations: string[] = [];
  
  // Check if we're on localhost
  if (window.location.hostname === 'localhost') {
    recommendations.push(`Add ${window.location.origin} to Azure AD app registration as Single-page application redirect URI`);
  }
  
  // Check current configuration
  recommendations.push('Ensure app is registered as "Single-page application" not "Web application"');
  recommendations.push('Enable "Access tokens" and "ID tokens" in Authentication settings');
  recommendations.push('Add all development and production URLs as redirect URIs');
  
  return {
    clientId: msalConfig.auth.clientId,
    authority: msalConfig.auth.authority,
    redirectUri: msalConfig.auth.redirectUri || window.location.origin,
    recommendations
  };
}

// Teams Presence specific configuration (separate app registration)
// Teams Presence MSAL configuration
const getTeamsPresenceConfig = () => {
  const clientId = config.appRegistrations.teamsPresenceClientId;
  
  if (!clientId) {
    console.error('[Teams Presence] Could not resolve a Teams Presence client ID at runtime');
    console.error('[Teams Presence] Build environment variables:', {
      hasClientId: !!import.meta.env.VITE_TEAMS_PRESENCE_CLIENT_ID,
      allViteVars: Object.keys(import.meta.env).filter(k => k.startsWith('VITE_'))
    });
    throw new Error('Teams Presence Client ID not configured. Set VITE_TEAMS_PRESENCE_CLIENT_ID during build or ensure /api/app-config/app-registrations returns TEAMS_PRESENCE_CLIENT_ID.');
  }
  
  return {
    auth: {
      clientId: clientId, // Teams Presence app registration
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'localStorage' as const,
    },
    system: {
      allowPlatformBroker: false,
    }
  };
};

// Teams Presence scopes
const teamsPresenceScopes = ['https://graph.microsoft.com/Presence.Read.All'];

// Initialize Teams Presence MSAL instance
let teamsPresenceMsalInstance: PublicClientApplication | null = null;
let teamsPresenceGraphClient: Client | null = null;

// Store external token provider for Teams Presence (if using Teams SSO)
let teamsPresenceExternalTokenProvider: TokenProvider | null = null;

// Set external token provider for Teams Presence (e.g., Teams SSO token)
export function setTeamsPresenceTokenProvider(tokenProvider: TokenProvider | null): void {
  teamsPresenceExternalTokenProvider = tokenProvider;
  // Reset Teams Presence graph client to force re-initialization
  teamsPresenceGraphClient = null;
  logger.debug('Teams Presence token provider updated', { hasProvider: !!tokenProvider }, 'GRAPH');
}

async function initializeTeamsPresenceMsal(): Promise<PublicClientApplication> {
  if (!teamsPresenceMsalInstance) {
    teamsPresenceMsalInstance = new PublicClientApplication(getTeamsPresenceConfig());
    await teamsPresenceMsalInstance.initialize();
    
    // Handle redirect result if returning from authentication
    try {
      const response = await teamsPresenceMsalInstance.handleRedirectPromise();
      if (response) {
        console.log('Teams Presence redirect authentication successful:', response);
      }
    } catch (error) {
      console.error('Error handling Teams Presence redirect:', error);
    }
  }
  
  return teamsPresenceMsalInstance;
}

// Initialize Teams Presence Graph client
// Now supports external token provider (Teams SSO) to avoid popup authentication
export async function initializeTeamsPresenceGraphClient(usePopup: boolean = false): Promise<Client> {
  if (teamsPresenceGraphClient && !usePopup) return teamsPresenceGraphClient;

  try {
    logger.debug('Initializing Teams Presence Graph client', { 
      useExternalToken: !!teamsPresenceExternalTokenProvider,
      usePopup 
    }, 'GRAPH');
    
    let accessToken: string;

    // If external token provider is available (e.g., Teams SSO), use it
    if (teamsPresenceExternalTokenProvider && !usePopup) {
      logger.debug('Using external token provider for Teams Presence (Teams SSO)', undefined, 'GRAPH');
      accessToken = await teamsPresenceExternalTokenProvider();
      logger.debug('External Teams Presence token acquired', { tokenLength: accessToken.length }, 'GRAPH');
    } else {
      // Use MSAL for Teams Presence authentication
      logger.debug('Using MSAL for Teams Presence authentication', undefined, 'GRAPH');
      
      const msalInstance = await initializeTeamsPresenceMsal();
      
      // Try to get token silently first (unless explicitly requesting popup)
      const accounts = msalInstance.getAllAccounts();
      logger.debug('Teams Presence accounts found', { count: accounts.length }, 'GRAPH');

      if (!usePopup && accounts.length > 0) {
        logger.debug('Attempting silent Teams Presence token acquisition', undefined, 'GRAPH');
        try {
          const response = await msalInstance.acquireTokenSilent({
            scopes: teamsPresenceScopes,
            account: accounts[0]!,
          });
          accessToken = response.accessToken;
          logger.debug('Silent Teams Presence token acquisition successful', undefined, 'GRAPH');
        } catch (error) {
          logger.debug('Silent Teams Presence token acquisition failed, trying popup', undefined, 'GRAPH');
          if (error instanceof InteractionRequiredAuthError) {
            const response = await msalInstance.acquireTokenPopup({
            scopes: teamsPresenceScopes,
            prompt: 'select_account',
          });
          accessToken = response.accessToken;
          logger.debug('Popup Teams Presence token acquisition successful', undefined, 'GRAPH');
        } else {
          throw error;
        }
      }
    } else {
      logger.debug('Using popup for Teams Presence authentication', undefined, 'GRAPH');
      const response = await msalInstance.acquireTokenPopup({
        scopes: teamsPresenceScopes,
        prompt: 'select_account',
      });
      accessToken = response.accessToken;
      logger.debug('Teams Presence sign-in successful', undefined, 'GRAPH');
    }
    }

    // Create Graph client for Teams Presence
    // For external tokens (Teams SSO), refresh token on each call
    teamsPresenceGraphClient = Client.init({
      authProvider: async (done) => {
        if (teamsPresenceExternalTokenProvider) {
          // For Teams SSO, get fresh token on each call
          try {
            const freshToken = await teamsPresenceExternalTokenProvider();
            done(null, freshToken);
          } catch (error: any) {
            done(error, null);
          }
        } else {
          // For MSAL, use cached token
          done(null, accessToken);
        }
      }
    });

    logger.debug('Teams Presence Graph client created successfully', undefined, 'GRAPH');
    return teamsPresenceGraphClient;
  } catch (error: any) {
    logger.error('Error initializing Teams Presence Graph client', error, 'GRAPH');
    throw new Error(`Teams Presence authentication failed: ${error.message}`);
  }
}

// Test Teams Presence API connectivity
export async function testTeamsPresenceConnection(userId?: string): Promise<{ success: boolean; message: string; details?: any }> {
  try {
    logger.debug('Testing Teams Presence connection', undefined, 'GRAPH');
    
    // Initialize Teams Presence Graph client with popup authentication
    const client = await initializeTeamsPresenceGraphClient(true);
    logger.debug('Teams Presence authentication successful', undefined, 'GRAPH');
    
    // Test basic presence endpoint
    const endpoint = userId ? `/users/${userId}/presence` : '/me/presence';
    logger.debug(`Testing presence endpoint`, { endpoint }, 'GRAPH');
    const presence = await client.api(endpoint).get();
    logger.debug('User presence retrieved', { availability: presence.availability }, 'GRAPH');
    
    return {
      success: true,
      message: `Teams Presence connection successful! ${userId ? `User ${userId}` : 'Your'} status: ${presence.availability || 'Unknown'}`,
      details: { 
        presence: presence,
        appId: getTeamsPresenceConfig().auth.clientId,
        scopes: teamsPresenceScopes
      }
    };
  } catch (error: any) {
    console.error('Teams Presence connection test failed:', error);
    
    let troubleshootingMessage = error.message;
    
    if (error.code === 'Forbidden' || error.message?.includes('Insufficient privileges')) {
      troubleshootingMessage = 'Admin consent required for Teams Presence. Administrator needs to grant Presence.Read.All permission for the Teams Presence app registration.';
    } else if (error.code === 'Unauthorized' || error.message?.includes('401')) {
      troubleshootingMessage = 'Teams Presence authentication failed. Please ensure the Teams Presence app registration is configured correctly.';
    } else if (error.message?.includes('consent')) {
      troubleshootingMessage = 'Teams Presence requires separate admin consent. Please use the Teams Presence consent button.';
    }
    
    return {
      success: false,
      message: `Teams Presence test failed: ${troubleshootingMessage}`,
      details: {
        errorCode: error.code,
        errorMessage: error.message,
        appId: getTeamsPresenceConfig().auth.clientId,
        requiredScopes: teamsPresenceScopes
      }
    };
  }
}

// Contact Sync configuration
const getContactSyncConfig = async () => {
  // Fetch Contact Sync client ID from API
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071'}/api/app-config/contact-sync-client-id`);
  const data = await response.json();
  
  return {
    auth: {
      clientId: data.clientId || '',
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'localStorage' as const,
    },
    system: {
      allowPlatformBroker: false,
    }
  };
};

// Contact Sync scopes (dedicated Contact Sync app registration).
const contactSyncScopes = [
  'https://graph.microsoft.com/Contacts.ReadWrite',
  'https://graph.microsoft.com/User.Read.All',
  'https://graph.microsoft.com/User.Read'
];

// Initialize Contact Sync MSAL instance
let contactSyncMsalInstance: PublicClientApplication | null = null;
let contactSyncGraphClient: Client | null = null;

async function initializeContactSyncMsal(): Promise<PublicClientApplication> {
  if (!contactSyncMsalInstance) {
    const syncConfig = await getContactSyncConfig();
    if (!syncConfig.auth.clientId) {
      throw new Error('Contact Sync Client ID not configured');
    }
    
    contactSyncMsalInstance = new PublicClientApplication(syncConfig);
    await contactSyncMsalInstance.initialize();
    
    // Handle redirect result if returning from authentication
    try {
      const response = await contactSyncMsalInstance.handleRedirectPromise();
      if (response) {
        console.log('Contact Sync redirect authentication successful:', response);
      }
    } catch (error) {
      console.error('Error handling Contact Sync redirect:', error);
    }
  }
  
  return contactSyncMsalInstance;
}

// Initialize Contact Sync Graph client
export async function initializeContactSyncGraphClient(usePopup: boolean = false): Promise<Client> {
  if (contactSyncGraphClient && !usePopup) return contactSyncGraphClient;

  try {
    logger.debug('Initializing Contact Sync Graph client', undefined, 'GRAPH');
    
    const msalInstance = await initializeContactSyncMsal();
    
    const accounts = msalInstance.getAllAccounts();
    logger.debug('Contact Sync accounts found', { count: accounts.length }, 'GRAPH');
    let accessToken: string;

    if (!usePopup && accounts.length > 0) {
      logger.debug('Attempting silent Contact Sync token acquisition', undefined, 'GRAPH');
      try {
        const response = await msalInstance.acquireTokenSilent({
          scopes: contactSyncScopes,
          account: accounts[0]!,
        });
        accessToken = response.accessToken;
        logger.debug('Silent Contact Sync token acquisition successful', undefined, 'GRAPH');
      } catch (error) {
        logger.debug('Silent Contact Sync token acquisition failed, trying popup', undefined, 'GRAPH');
        if (error instanceof InteractionRequiredAuthError) {
          const response = await msalInstance.acquireTokenPopup({
            scopes: contactSyncScopes,
            prompt: 'select_account',
          });
          accessToken = response.accessToken;
          logger.debug('Popup Contact Sync token acquisition successful', undefined, 'GRAPH');
        } else {
          throw error;
        }
      }
    } else {
      logger.debug('Using popup for Contact Sync authentication', undefined, 'GRAPH');
      const response = await msalInstance.acquireTokenPopup({
        scopes: contactSyncScopes,
        prompt: 'select_account',
      });
      accessToken = response.accessToken;
      logger.debug('Contact Sync sign-in successful', undefined, 'GRAPH');
    }

    contactSyncGraphClient = Client.init({
      authProvider: async (done) => {
        done(null, accessToken);
      }
    });

    logger.debug('Contact Sync Graph client created successfully', undefined, 'GRAPH');
    return contactSyncGraphClient;
  } catch (error: any) {
    logger.error('Error initializing Contact Sync Graph client', error, 'GRAPH');
    throw new Error(`Contact Sync authentication failed: ${error.message}`);
  }
}

// Test Contact Sync API connectivity
export async function testContactSyncConnection(userId?: string): Promise<{ success: boolean; message: string; details?: any }> {
  try {
    logger.debug('Testing Contact Sync connection', { userId }, 'GRAPH');
    
    // Initialize Contact Sync Graph client with popup authentication
    const client = await initializeContactSyncGraphClient(true);
    logger.debug('Contact Sync authentication successful', undefined, 'GRAPH');
    
    // Test basic endpoints
    const userEndpoint = userId ? `/users/${userId}` : '/me';
    logger.debug('Testing user endpoint', { endpoint: userEndpoint }, 'GRAPH');
    const user = await client.api(userEndpoint).get();
    
    // Test contacts endpoint
    const contactsEndpoint = userId ? `/users/${userId}/contacts` : '/me/contacts';
    logger.debug('Testing contacts endpoint', { endpoint: contactsEndpoint }, 'GRAPH');
    const contacts = await client.api(contactsEndpoint).top(1).get();
    logger.debug('Contacts access test successful', undefined, 'GRAPH');
    
    return {
      success: true,
      message: `Contact Sync connection successful! Can access ${userId ? `user ${user.displayName || userId}` : 'your'} profile and contacts.`,
      details: { 
        user: user.displayName || user.userPrincipalName,
        appId: (await getContactSyncConfig()).auth.clientId,
        scopes: contactSyncScopes
      }
    };
  } catch (error: any) {
    console.error('Contact Sync connection test failed:', error);
    
    let troubleshootingMessage = error.message;
    
    if (error.code === 'Forbidden' || error.message?.includes('Insufficient privileges')) {
      troubleshootingMessage = 'Admin consent required for Contact Sync. Administrator needs to grant Contacts.ReadWrite, User.Read.All, and User.Read permissions.';
    } else if (error.code === 'Unauthorized' || error.message?.includes('401')) {
      troubleshootingMessage = 'Contact Sync authentication failed. Please ensure the Contact Sync app registration is configured correctly.';
    } else if (error.message?.includes('consent')) {
      troubleshootingMessage = 'Contact Sync requires admin consent. Please use the Contact Sync consent button.';
    } else if (error.message?.includes('not configured')) {
      troubleshootingMessage = 'Contact Sync Client ID is not configured in the Hosting Portal settings. Please configure it first.';
    }
    
    return {
      success: false,
      message: `Contact Sync test failed: ${troubleshootingMessage}`,
      details: {
        errorCode: error.code,
        errorMessage: error.message,
        appId: (await getContactSyncConfig()).auth.clientId,
        requiredScopes: contactSyncScopes
      }
    };
  }
}

// Debug function to test authentication and basic Graph API access
export async function testGraphConnection(): Promise<{ success: boolean; message: string; details?: any }> {
  // Add timeout to prevent hanging
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('Connection test timed out after 30 seconds')), 30000);
  });

  try {
    console.log('Testing Graph connection...');
    console.log('Current configuration:', checkAppRegistrationConfig());
    
    // Test authentication with timeout
    const client = await Promise.race([initializeGraphClient(), timeoutPromise]);
    console.log('Authentication successful');
    
    // Test basic Graph API call (get current user)
    console.log('Testing /me endpoint...');
    const me = await client.api('/me').get();
    console.log('Current user:', me);
    
    // Test users endpoint with minimal query
    console.log('Testing /users endpoint...');
    const users = await client.api('/users').select('id,displayName').top(1).get();
    console.log('Users test:', users);
    
    return {
      success: true,
      message: `Connection successful! Signed in as: ${me.displayName || me.userPrincipalName}`,
      details: { currentUser: me, usersCount: users.value.length }
    };
  } catch (error: any) {
    console.error('Graph connection test failed:', error);
    
    // Provide specific guidance for common issues
    let troubleshootingMessage = error.message;
    
    // Check for specific Graph API errors
    if (error.code === 'Forbidden' || error.message?.includes('Insufficient privileges')) {
      troubleshootingMessage = 'Admin consent required. Your organization administrator needs to grant permissions for User.Read.All and Group.Read.All scopes.';
    } else if (error.code === 'Unauthorized' || error.message?.includes('401')) {
      troubleshootingMessage = 'Authentication token invalid. Please sign out and sign in again.';
    } else if (error.message?.includes('Azure AD app registration')) {
      const config = checkAppRegistrationConfig();
      troubleshootingMessage += `\n\nConfiguration check:\n- Client ID: ${config.clientId}\n- Redirect URI: ${config.redirectUri}\n- Recommendations:\n${config.recommendations.map(r => `  • ${r}`).join('\n')}`;
    }
    
    return {
      success: false,
      message: `Connection failed: ${troubleshootingMessage}`,
      details: {
        errorCode: error.code,
        errorMessage: error.message,
        statusCode: error.status || error.statusCode,
        fullError: error
      }
    };
  }
}

// ==================== Profile Photo Management ====================

const PHOTO_CACHE_KEY = 'quickdial_profile_photos';
const PHOTO_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface PhotoCache {
  [userId: string]: {
    base64Data: string;
    timestamp: number;
  };
}

// In-memory L1 cache: avoids repeated localStorage reads and reduces quota pressure.
// Keys are userId; values are ready-to-use data: URLs.
const photoMemoryCache = new Map<string, { dataUrl: string; timestamp: number }>();

function getPhotoCache(): PhotoCache {
  try {
    const cached = localStorage.getItem(PHOTO_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (error) {
    console.error('Error reading photo cache:', error);
    return {};
  }
}

function setPhotoCache(cache: PhotoCache): void {
  try {
    // Prune expired entries before writing to keep storage size bounded.
    const now = Date.now();
    const pruned: PhotoCache = {};
    for (const [userId, entry] of Object.entries(cache)) {
      if (now - entry.timestamp < PHOTO_CACHE_DURATION) {
        pruned[userId] = entry;
      }
    }
    localStorage.setItem(PHOTO_CACHE_KEY, JSON.stringify(pruned));
  } catch {
    // Quota exceeded or storage unavailable — the in-memory cache still
    // serves photos for the current session; no action needed.
  }
}

// Helper: Convert ArrayBuffer to base64
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chars: string[] = [];
  for (let i = 0; i < bytes.byteLength; i++) {
    chars.push(String.fromCharCode(bytes[i]!));
  }
  return btoa(chars.join(''));
}

// Build a data: URL from a base64 photo string (CSP-safe, no blob: URLs).
function base64ToDataUrl(base64Data: string): string {
  return `data:image/jpeg;base64,${base64Data}`;
}

// Clear photo cache (for settings).
// Clears the in-memory L1 cache as well as the localStorage L2 entry, so the
// next call to getUserPhoto/getUserPhotos will re-fetch from the network.
export function clearPhotoCache(): void {
  photoMemoryCache.clear();
  try {
    localStorage.removeItem(PHOTO_CACHE_KEY);
    console.log('Photo cache cleared');
  } catch (error) {
    console.error('Error clearing photo cache:', error);
  }
}

// Get a single user's profile photo via backend API
export async function getUserPhoto(userId: string): Promise<string | null> {
  // Check in-memory L1 cache first
  const memoryCached = photoMemoryCache.get(userId);
  if (memoryCached && Date.now() - memoryCached.timestamp < PHOTO_CACHE_DURATION) {
    console.log(`Using cached photo for user ${userId}`);
    return memoryCached.dataUrl;
  }

  // Fall back to persistent L2 cache (localStorage)
  const cache = getPhotoCache();
  const cached = cache[userId];
  if (cached && Date.now() - cached.timestamp < PHOTO_CACHE_DURATION) {
    console.log(`Using cached photo for user ${userId}`);
    const dataUrl = base64ToDataUrl(cached.base64Data);
    photoMemoryCache.set(userId, { dataUrl, timestamp: cached.timestamp });
    return dataUrl;
  }

  try {
    // Fetch photo via backend API (no direct Graph permissions needed)
    if (!authHeaderProvider) {
      console.warn('Cannot fetch photo - no auth header provider set');
      return null;
    }

    const token = await authHeaderProvider();
    const response = await fetch(`${config.api.baseUrl}/api/user-photos/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log(`No photo available for user ${userId}`);
      } else {
        console.warn(`Failed to fetch photo for user ${userId}: ${response.status}`);
      }
      return null;
    }

    // Convert response to base64 for caching
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    const base64Data = arrayBufferToBase64(arrayBuffer);
    const dataUrl = base64ToDataUrl(base64Data);
    const timestamp = Date.now();

    // Populate both caches
    photoMemoryCache.set(userId, { dataUrl, timestamp });
    cache[userId] = { base64Data, timestamp };
    setPhotoCache(cache);

    console.log(`Fetched and cached photo for user ${userId}`);
    return dataUrl;
  } catch (error: any) {
    console.warn(`Error fetching photo for user ${userId}:`, error.message);
    return null;
  }
}

// Per-request caps enforced by the API (GraphIntegrationService.MaxPhotoBatchSize / MaxPresenceBatchSize).
const PHOTO_BATCH_SIZE = 100;
const PRESENCE_BATCH_SIZE = 650;

function chunkIds(ids: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let index = 0; index < ids.length; index += size) {
    chunks.push(ids.slice(index, index + size));
  }
  return chunks;
}

// Batch fetch user photos efficiently
export async function getUserPhotos(userIds: string[]): Promise<Record<string, string>> {
  if (!userIds || userIds.length === 0) {
    return {};
  }

  const cache = getPhotoCache();
  const results: Record<string, string> = {};
  const toFetch: string[] = [];

  // Check caches first (L1 then L2)
  for (const userId of userIds) {
    const memoryCached = photoMemoryCache.get(userId);
    if (memoryCached && Date.now() - memoryCached.timestamp < PHOTO_CACHE_DURATION) {
      results[userId] = memoryCached.dataUrl;
      continue;
    }

    const cached = cache[userId];
    if (cached && Date.now() - cached.timestamp < PHOTO_CACHE_DURATION) {
      const dataUrl = base64ToDataUrl(cached.base64Data);
      photoMemoryCache.set(userId, { dataUrl, timestamp: cached.timestamp });
      results[userId] = dataUrl;
    } else {
      toFetch.push(userId);
    }
  }

  if (toFetch.length === 0) {
    console.log(`All ${userIds.length} photos loaded from cache`);
    return results;
  }

  console.log(`Loading ${toFetch.length} photos from backend API (${userIds.length - toFetch.length} from cache)`);

  try {
    // Fetch photos via backend API (no direct Graph permissions needed)
    if (!authHeaderProvider) {
      console.warn('Cannot fetch photos - no auth header provider set');
      return results;
    }

    const token = await authHeaderProvider();
    for (const chunk of chunkIds(toFetch, PHOTO_BATCH_SIZE)) {
      const response = await fetch(`${config.api.baseUrl}/api/user-photos/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userIds: chunk }),
      });

      if (!response.ok) {
        console.error(`Failed to fetch photos batch: ${response.status}`);
        continue;
      }

      const data = await response.json();
      const { photos } = data;
      const timestamp = Date.now();

      // Process fetched photos
      for (const [userId, base64Data] of Object.entries(photos)) {
        if (base64Data && typeof base64Data === 'string') {
          const dataUrl = base64ToDataUrl(base64Data);
          photoMemoryCache.set(userId, { dataUrl, timestamp });
          cache[userId] = { base64Data, timestamp };
          results[userId] = dataUrl;
          console.log(`✅ Photo loaded for user ${userId}`);
        } else {
          console.log(`No photo available for user ${userId}`);
        }
      }
    }

    setPhotoCache(cache);
  } catch (error: any) {
    console.error('Error in batch photo loading:', error);
  }

  console.log(`📊 Loaded ${Object.keys(results).length} photos total`);
  return results;
}

// ==================== Teams Presence Management ====================

// Fetch Teams Presence information via backend API
// Uses admin-consented application permissions (no user consent needed)
export async function getUserPresence(userIds: string[]): Promise<Record<string, { availability: string; activity: string }>> {
  try {
    if (!userIds || userIds.length === 0) {
      return {};
    }

    console.log('🔄 Fetching Teams Presence via backend API for users:', userIds);

    // Get auth token
    if (!authHeaderProvider) {
      console.warn('⚠️ No auth header provider set for presence API');
      return {};
    }

    const token = await authHeaderProvider();
    if (!token) {
      console.warn('⚠️ Failed to get auth token for presence API');
      return {};
    }

    const presenceData: Record<string, { availability: string; activity: string }> = {};

    // Call backend API which uses admin-consented app permissions
    for (const chunk of chunkIds(userIds, PRESENCE_BATCH_SIZE)) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/presence/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ userIds: chunk }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ message: response.statusText }));
          console.warn('⚠️ Failed to fetch presence from backend:', error.message || response.statusText);
          continue;
        }

        const presences = await response.json();
        if (Array.isArray(presences)) {
          for (const presence of presences) {
            if (presence.id) {
              presenceData[presence.id] = {
                availability: presence.availability || 'PresenceUnknown',
                activity: presence.activity || 'PresenceUnknown'
              };
              console.log(`✅ Presence for ${presence.id}: ${presence.availability}`);
            }
          }
        }
      } catch (error) {
        console.warn('⚠️ Error fetching presence chunk:', error);
      }
    }

    console.log('📊 Final presence data:', presenceData);
    return presenceData;
  } catch (error) {
    console.error('❌ Error fetching Teams Presence:', error);
    // Return empty object on error - no presence icons will show
    return {};
  }
}
